// oxlint-disable no-extraneous-class -- class is used to create context builders
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { GlobalAppStateStoreType } from '../../../stores/app/GlobalAppStateStore';
import type { GlobalStateStoreType } from '../../../stores/GlobalStateStore';
import type {
  RepeatableActions,
  RuntimeSourceInfo,
} from '../../../utils/repeatables/attachInstances';
import type { RowScope } from '../../../utils/repeatables/attachRowScopeIdentities';
import type ContextBuilder from './ContextBuilder';
import type { ContextMethodsState, ContextState } from './types';

/**
 * Factory for creating pre-configured context builders
 */
class ContextFactory {
  /**
   * Creates a context builder for app-level context
   */
  static createAppContextBuilder(
    {
      appState,
      interfaceRecord,
      dataSourceMethods,
      localStorageMethods,
      sessionStorageMethods,
      pageVariableMethods,
      pageFunctionMethods,
      actions,
      queryMeta,
      walkthrough,
    }: {
      appState: Pick<
        GlobalAppStateStoreType,
        | 'envVariables'
        | 'userContext'
        | 'deviceContext'
        | 'appsAndIntegrations'
        | 'location'
        | 'dataSources'
        | 'dataSourcesMeta'
        | 'localStorage'
        | 'sessionStorage'
        | 'pageVariables'
        | 'pageFunctions'
        | 'colorSchemeDetails'
        | 'activeTheme'
        | 'availableThemes'
        | 'navigation'
      >;
      interfaceRecord: InterfaceEntity | undefined;
    } & Omit<ContextMethodsState, 'blockMethods'> &
      Pick<ContextState, 'queryMeta' | 'walkthrough'>,
    contextBuilder: ContextBuilder,
  ): ContextBuilder {
    return contextBuilder
      .setPageVariables(appState.pageVariables)
      .setPageFunctions(appState.pageFunctions)
      .setMethods({
        blockMethods: {},
        dataSourceMethods,
        pageVariableMethods,
        pageFunctionMethods,
        localStorageMethods,
        sessionStorageMethods,
        actions,
      })
      .setLocation(appState.location)
      .setUserContext(appState.userContext)
      .setDeviceContext(appState.deviceContext)
      .setAppsAndIntegrations(appState.appsAndIntegrations)
      .setLocalStorage(appState.localStorage)
      .setSessionStorage(appState.sessionStorage)
      .setInterfaceRecord(interfaceRecord)
      .setEnvVariables(appState.envVariables)
      .setDataSources(appState.dataSources)
      .setDataSourcesMeta(appState.dataSourcesMeta)
      .setQueryMeta(queryMeta)
      .setColorScheme(appState.colorSchemeDetails.effectiveColorScheme)
      .setActiveTheme(appState.activeTheme)
      .setAvailableThemes(appState.availableThemes)
      .setWalkthrough(walkthrough);
  }

  /**
   * Creates a context builder for page-level context.
   * Callers must pass `interfaceRecord` from getInterfaceRecordForComputeContext (or another
   * source that already set properties.computed_navigation) whenever a record is present.
   */
  static createPageContextBuilder(
    {
      state,
      appState,
      interfaceRecord,
      blocksRuntimeState,
      blockMethods,
      dataSourceMethods,
      pageVariableMethods,
      pageFunctionMethods,
      localStorageMethods,
      sessionStorageMethods,
      mediaUploads,
      queryMeta,
      actions,
      activeAppVariantKey,
      walkthrough,
      runtimeSourceInfo,
      rowScopeStack,
      repeatableActions,
      runtimeSourceBlockIds,
    }: {
      state: Pick<
        GlobalStateStoreType,
        | 'blocks'
        | 'blockRefs'
        | 'pageInputs'
        | 'pageParams'
        | 'pageVariables'
        | 'pageFunctions'
        | 'dataSources'
        | 'dataSourcesMeta'
      >;
      runtimeSourceInfo?: RuntimeSourceInfo;
      rowScopeStack?: readonly RowScope[];
      repeatableActions?: RepeatableActions;
      runtimeSourceBlockIds?: ReadonlySet<string>;
      appState: Pick<
        GlobalAppStateStoreType,
        | 'pageVariables'
        | 'pageFunctions'
        | 'envVariables'
        | 'location'
        | 'userContext'
        | 'deviceContext'
        | 'appsAndIntegrations'
        | 'localStorage'
        | 'sessionStorage'
        | 'colorSchemeDetails'
        | 'dataSources'
        | 'dataSourcesMeta'
        | 'activeTheme'
        | 'availableThemes'
      >;
      interfaceRecord: InterfaceEntity | undefined;
      activeAppVariantKey?: string;
      blocksRuntimeState?: Record<string, Record<string, unknown>>;
    } & ContextMethodsState &
      Pick<ContextState, 'mediaUploads' | 'queryMeta' | 'walkthrough'>,
    contextBuilder: ContextBuilder,
  ): ContextBuilder {
    return contextBuilder
      .setBlockStates(state.blocks)
      .setBlockRefs(state.blockRefs)
      .setPageInputs(state.pageInputs)
      .setPageParams(state.pageParams)
      .setMethods({
        blockMethods,
        dataSourceMethods,
        pageVariableMethods,
        pageFunctionMethods,
        localStorageMethods,
        sessionStorageMethods,
        actions,
      })
      .setActiveAppVariantKey(activeAppVariantKey)
      .setDataSources({ ...appState.dataSources, ...state.dataSources })
      .setDataSourcesMeta({ ...appState.dataSourcesMeta, ...state.dataSourcesMeta })
      .setMediaUploads(mediaUploads)
      .setQueryMeta(queryMeta)
      .setPageVariables({ ...appState.pageVariables, ...state.pageVariables })
      .setPageFunctions({ ...appState.pageFunctions, ...state.pageFunctions })
      .setLocation(appState.location)
      .setUserContext(appState.userContext)
      .setDeviceContext(appState.deviceContext)
      .setAppsAndIntegrations(appState.appsAndIntegrations)
      .setLocalStorage(appState.localStorage)
      .setSessionStorage(appState.sessionStorage)
      .setInterfaceRecord(interfaceRecord)
      .setEnvVariables(appState.envVariables)
      .setColorScheme(appState.colorSchemeDetails.effectiveColorScheme)
      .setActiveTheme(appState.activeTheme)
      .setAvailableThemes(appState.availableThemes)
      .setWalkthrough(walkthrough)
      .setBlocksRuntimeState(blocksRuntimeState)
      .setRuntimeSourceInfo(runtimeSourceInfo)
      .setRowScopeStack(rowScopeStack)
      .setRepeatableActions(repeatableActions)
      .setRuntimeSourceBlockIds(runtimeSourceBlockIds);
  }
}

export default ContextFactory;
